import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import update from "immutability-helper";
import Mousetrap from "mousetrap";
import { get, filter as lodashFilter, size, map, flatten, slice } from "lodash";

import { fetchPut, fetchPost } from "../../api/fetch";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Tooltip from "@material-ui/core/Tooltip";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import TablePagination from "@material-ui/core/TablePagination";
import CircularProgress from "@material-ui/core/CircularProgress";

import DateFilter from "./DateFilter";
import Loader from "../Common/Loader";
import Picture from "../Common/Picture";
import TagColor from "../Common/TagColor";
import SmartSearch from "../Common/SmartSearch";
import SmartSearchRef from "../Common/SmartSearchRef";
import LiveCardAction from "./LiveEvents/LiveCardAction.js";
import TablePaginationActions from "./TablePaginationActions";
import ProfileSurveyDialog from "../Profiles/ProfileSurvey/ProfileSurveyDialog";
import SiteLogCsvExport from "./SiteLogCsvExport";

import {
  addTab,
  addLogFilter,
  sort,
  removeOldestDynamicTab,
  //setLogsPage,
  setLogsRowsPerPage,
  setCollapsed,
  updateOneLog,
  fetchLogsTabNew,
} from "../../actions/siteEntranceExit/tabs";

import { fetchLog } from "../../utils/eventGroups";
import { isUnknownProfile } from "../../utils/profile";
import { getAttributeName } from "../../utils/attributes";

import { driveInOut, trainInOut, walkInOut } from "../../constants/reducers";
import {
  COVID_VERIFICATION,
  VERIFICATION_WARNING,
} from "../../constants/eventGroups";
import { DYNAMIC_TAB } from "../../constants/siteEntranceExit";
import {
  driver,
  human,
  truck,
  trailer,
  container,
  date,
} from "../../constants/common";
import { measuredTemperature } from "../../constants/liveEvents";

import {
  driveInOutFields,
  trainInOutFields,
  walkInOutFields,
} from "../../resources/SiteLogFields";

import images from "../../assets/img/images";
import colors from "../../assets/style/colors";
import { nowWithOutSeconds } from "../../utils/date";

const styles = {
  mainContentLarge: {
    borderRadius: "4px",
    overflow: "auto",
    maxHeight: "calc(100vh - 296px)",
  },
  mainContentShort: {
    overflow: "auto",
    minHeight: "250px",
    maxHeight: "calc(100vh - 370px)",
    //maxHeight: "calc(100vh - 637px)",
  },
  tableShort: {
    maxWidth: "1440px",
    margin: "auto",
  },
  tableContainer: {
    overflowX: "auto",
    maxWidth: "calc(100vw - 113px)",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    "& div": {
      marginTop: "5px",
    },
  },
  headerGroupCell: {
    height: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& img": {
      marginRight: "5px",
      maxWidth: "40px",
    },
  },
  tableHeaderCell: {
    fontWeight: "500",
    borderBottom: `3px solid ${colors.white}`,
  },
  tableHeader: {
    margin: "0",
    display: "inline-block",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
    "& img": {
      marginLeft: "5px",
      marginBottom: "2px",
    },
    "& p": {
      margin: 0,
      display: "inline",
    },
  },
  headerIcon: {
    marginLeft: "0px !important",
    marginBottom: "0px !important",
  },
  selectFilter: {
    width: "100%",
    margin: 0,
    fontWeight: "normal",
    textAlign: "left",
  },
  selectValue: {
    padding: "5px",
    paddingRight: "15px",
    fontSize: "0.875em",
    height: "26px",
    lineHeight: "26px",
  },
  selectMenu: {
    padding: 0,
    paddingTop: "9.5px",
    paddingBottom: "9.5px",
    "& li": {
      padding: 0,
      paddingRight: "10px",
    },
  },
  loadMore: {
    margin: "10px 0px",
    textAlign: "center",
    "& span": {
      fontWeight: "500",
      color: colors.primary,
      cursor: "pointer",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  tableRowHover: {
    "&:hover": {
      backgroundColor: colors.lightGreenBg + " !important",
    },
  },
  tableCell: {
    borderBottom: 0,
    color: colors.black54,
    height: "50px",
    maxHeight: "50px",
    paddingTop: "0px !important",
    paddingBottom: "0px !important",
  },
  stickyHeader: {
    left: "initial",
    top: "37px",
  },
  stickyGroupCell: {
    left: "initial",
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
  },
  tooltip: {
    fontSize: "1em",
    marginTop: "0px",
  },
  paginationToolbar: {
    minHeight: "50px",
  },
  verifiedHeader: {
    "& img": {
      marginRight: "10px",
    },
  },
  loader: {
    height: "calc(100vh - 420px)",
    display: "flex",
    borderBottom: "1px solid " + colors.veryLighPink,
  },
  listItemText: {
    fontSize: "0.875em",
    color: colors.black54,
  },
  noPhotoBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${colors.veryLighPink}`,
    width: "44px",
    height: "44px",
    backgroundColor: colors.veryLighPink,
    borderRadius: "4px",
  },
};

class SiteLogTable extends Component {
  mounted = false;
  initialized = false;

  constructor(props) {
    super(props);
    this.state = {
      offset: 0,
      zones: props.zones,
      number: props.displayNb || 10,
      loadMore: 10,
      page: props.pageNb || 0,
      rowsPerPage: props.rowsPerPage
        ? props.rowsPerPage
        : props.filters
        ? 10
        : 100000,
      emptyRows: 0,
      refreshing: false,
      // open: {
      //   main: true,
      //   verified: {}
      // }
    };
  }

  componentDidMount() {
    this.mounted = true;
    // console.log("SiteLogTable >> mounted.")
    Mousetrap.bind(
      "ctrl",
      () => {
        if (this.mounted && !this.state.ctrl) {
          this.setState({ ctrl: true });
        }
      },
      "keydown"
    );
    Mousetrap.bind(
      "ctrl",
      () => {
        if (this.mounted) this.setState({ ctrl: false });
      },
      "keyup"
    );
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleSelectFilter = async (values, filter, parent) => {
    if (
      values.includes("all") &&
      !this.props.filters[parent][filter].filter.includes("all")
    ) {
      values = ["all"];
    } else if (
      values.includes("all") &&
      this.props.filters[parent][filter].filter.includes("all")
    ) {
      values.splice(0, 1);
    }
    this.setState({ page: 0 });
    this.props.dispatch(
      addLogFilter(
        this.props.section,
        this.props.tabIndex,
        parent,
        filter,
        update(this.props.filters[parent][filter], { filter: { $set: values } })
      )
    );
  };

  handleSearchFilter = async (value, filter) => {
    this.setState({ page: 0 });
    this.props.dispatch(
      addLogFilter(
        this.props.section,
        this.props.tabIndex,
        filter[0],
        filter[1],
        update(this.props.filters[filter[0]][filter[1]], {
          filter: { $set: value },
        })
      )
    );
  };

  handleSearchFilterRef = async (value, filter) => {
    if (parseInt(value)) {
      try {
        let row = await fetchPost("new/event_groups", {
          filter: {
            ids: [parseInt(value)],
          },
          data: ["id", "parent", "type"],
        });

        let searchId = parseInt(value);
        if (![2, 3, 4, 5].includes(get(row, ["items", 0, "type", "id"]))) {
          searchId = parseInt(get(row, ["items", 0, "parent", "id"]));
        }

        let log = await fetchLog(
          this.props.section,
          { ids: [searchId] },
          this.props.language
        );
        this.openTab(log);
      } catch (e) {
        console.log(e);
      }
    }
  };

  handleDateFilter = async (filter, parent, startDate, endDate) => {
    /*this.setState({ page: 0 });
    this.props.dispatch(
      addLogFilter(
        this.props.section,
        this.props.tabIndex,
        parent,
        filter,
        update(this.props.filters[parent][filter], {
          startDate: { $set: startDate },
          endDate: { $set: endDate },
        })
      )
    );*/
    this.handleFilter(parent, filter, date, { startDate, endDate });
  };

  handleFilter = (field, subField, filterType, filterParams) => {
    if (filterType === date) {
      let newFilters = update(this.props.filters, {
        [field]: {
          [subField]: {
            startDate: { $set: filterParams.startDate },
            endDate: { $set: filterParams.endDate },
          },
        },
      });
      this.props.dispatch(
        fetchLogsTabNew(this.props.section, 0, newFilters, 0)
      );
      this.changePage(0);
    }
  };

  handleCheckboxChange = async (field, row, checked) => {
    let event;
    let createdOn;
    row.complete.events.forEach((e) => {
      if (
        get(e, ["profile", "type", "id"]) === 2 &&
        [60, 61].includes(get(e, ["type", "id"])) &&
        (!createdOn || get(e, ["created_on"]) > createdOn)
      ) {
        event = e;
        createdOn = get(e, ["created_on"]);
      }
    });
    if (event) {
      try {
        if (field === "privateVehicle") {
          await fetchPut(`events/${event.id}/attributes/106`, {
            value: checked ? 55 : 56,
          });
        } else if (field === "tailgating") {
          await fetchPut(`events/${event.id}/attributes/139`, {
            value: checked,
          });
        }
        this.props.dispatch(
          updateOneLog(
            this.props.section,
            null,
            this.props.short ? this.props.tabIndex : null,
            row.id
          )
        );
      } catch (e) {
        console.log(e);
      }
    }
  };

  calculateEmptyRows = (dataLength, page) => {
    return (
      this.props.rowsPerPage -
      Math.min(
        this.props.rowsPerPage,
        dataLength - page * this.props.rowsPerPage
      )
    );
  };

  changePage = async (newPage) => {
    this.setState({
      page: newPage,
    });
    const siteId = parseInt(get(this.props, ["match", "params", "siteId"]));
    this.props.history.push(
      `/siteEntranceExit/${this.props.section}/site/${siteId}?page=${newPage}`
    );
  };

  changeRowsPerPage = async (event) => {
    let rowsPerPage = parseInt(event.target.value, 10);
    this.setState({
      page: 0,
      //rowsPerPage
    });
    /*if (rowsPerPage > this.props.logs.length) {
      this.props.fetchMoreData(rowsPerPage - this.props.logs.length);
    }
    this.props.dispatch(
      setLogsPage(this.props.section, this.props.tabIndex, 0)
    );*/
    try {
      this.props.dispatch(
        setLogsRowsPerPage(this.props.section, this.props.tabIndex, rowsPerPage)
      );
    } catch (e) {
      console.log(e);
    }
  };

  openTab = (row, fromSection, fromField) => {
    let remainingSpace = window.innerWidth - 200 * this.props.openedTabsNb;

    // Not enough space, delete the oldest dynamic tab
    if (remainingSpace < 220) {
      this.props.dispatch(removeOldestDynamicTab(this.props.section));
    }

    if (!Boolean(this.state.ctrl)) {
      const siteId = parseInt(get(this.props, ["match", "params", "siteId"]));
      const eventGroupId = parseInt(row.id);
      this.props.history.push(
        `/siteEntranceExit/${this.props.section}/site/${siteId}/eventGroup/${eventGroupId}`
      );
    } else {
      // Add the new tab
      this.props.dispatch(
        addTab(
          this.props.section,
          null,
          {
            type: DYNAMIC_TAB,
            eventGroupId: row.id,
            status: row.complete.type.id === 3 ? "exit" : "entry",
            data: row,
            openFromTab: this.props.tabIndex,
            zone: {
              id: row.main.zoneId,
              name: row.main.zone,
            },
          },
          !Boolean(this.state.ctrl),
          true,
          fromField === "verification" ? fromField : fromSection
        )
      );
    }
  };

  renderHeaderGroupCell = (field, index) => {
    let subFields =
      this.props.section === driveInOut
        ? driveInOutFields.subFields
        : this.props.section === walkInOut
        ? walkInOutFields.subFields
        : this.props.section === trainInOut
        ? trainInOutFields.subFields
        : null;
    let verified =
      this.props.section === driveInOut
        ? driveInOutFields.verified
        : this.props.section === walkInOut
        ? walkInOutFields.verified
        : this.props.section === trainInOut
        ? trainInOutFields.verified
        : null;
    if (
      !this.props.displayColumns ||
      size(get(this.props.displayColumns, [index])) === 0
    ) {
      return null;
    }
    return (
      <TableCell
        key={field.name}
        classes={{ stickyHeader: this.props.classes.stickyGroupCell }}
        onClick={() =>
          this.props.dispatch(
            setCollapsed(
              this.props.section,
              update(this.props.open, {
                [field.name]: { $set: !this.props.open[field.name] },
                verified: {
                  [field.name]: { $set: false },
                },
              })
            )
          )
        }
        align="center"
        colSpan={
          !this.props.open[field.name] && field.collapsedNb
            ? field.collapsedNb
            : this.props.open.verified[field.name]
            ? subFields[field.subFieldsId].length + verified.length
            : subFields[field.subFieldsId].length
        }
        style={{
          borderTop: "1px solid " + colors.white,
          backgroundColor: colors.white,
          backgroundClip: "padding-box",
          cursor: field.collapsedNb ? "pointer" : "default",
          borderLeft: index !== 0 ? "1px solid " + colors.black : "none",
          minWidth: field.name.includes(trailer) ? "180px" : "150px",
          //borderBottom: `3px solid ${colors.white}`,
        }}
      >
        <Box className={this.props.classes.headerGroupCell}>
          {field.img && <img src={field.img} alt={field.name + "_img"} />}
          {I18n.get("site_" + field.name)}
          {field.collapsedNb && (
            <Box component="span" style={{ marginLeft: "10px" }}>
              {this.props.open[field.name] ? (
                <img src={images.rightArrow} alt="right_arrow" />
              ) : (
                <img src={images.leftArrow} alt="left_arrow" />
              )}
            </Box>
          )}
        </Box>
      </TableCell>
    );
  };

  renderHeader = (filter, parent, color = colors.cardTitleBg) => {
    let subFields =
      this.props.section === driveInOut
        ? driveInOutFields.subFields
        : this.props.section === walkInOut
        ? walkInOutFields.subFields
        : this.props.section === trainInOut
        ? trainInOutFields.subFields
        : null;
    return (
      <TableCell
        key={filter.name}
        classes={{
          root: this.props.classes.tableHeaderCell,
          stickyHeader: !this.props.short
            ? this.props.classes.stickyHeader
            : "",
        }}
        align={
          ["tailgating", "privateVehicle", "mask", "temperature"].includes(
            filter.name
          ) && this.props.short
            ? "center"
            : "left"
        }
        style={{
          lineHeight: this.props.short ? "0.2rem" : "1rem",
          height: this.props.short ? "50px" : "70px",
          minWidth: filter.minWidth || "150px",
          maxWidth: filter.minWidth || "150px",
          verticalAlign: "middle",
          borderLeft:
            filter.name === subFields[0][0].name
              ? "none"
              : filter.name === subFields[parent.subFieldsId][0].name &&
                !this.props.short
              ? "1px solid " + colors.black
              : "3px solid " + colors.white,
          //: "1px solid " + colors.veryLighPink,
          backgroundColor: color,
          backgroundClip: "padding-box",
        }}
      >
        <Box
          className={this.props.classes.tableHeader + " text-87"}
          style={{ paddingBottom: this.props.short ? "0px" : "2px" }}
          onClick={() =>
            filter.name !== "verified"
              ? this.props.dispatch(
                  sort(
                    this.props.section,
                    this.props.tabIndex,
                    parent.name,
                    filter.name
                  )
                )
              : this.props.dispatch(
                  setCollapsed(
                    this.props.section,
                    update(this.props.open, {
                      verified: {
                        [parent.name]: {
                          $set: !this.props.open.verified[parent.name],
                        },
                      },
                    })
                  )
                )
          }
        >
          {filter.name === "verified" ? (
            <>
              {I18n.get("site_" + filter.name)}
              <Box component="span" style={{ marginLeft: "10px" }}>
                {this.props.open.verified[parent.name] ? (
                  <img src={images.rightArrow} alt="right_arrow" />
                ) : (
                  <img src={images.leftArrow} alt="left_arrow" />
                )}
              </Box>
            </>
          ) : filter.name === "mask" ? (
            <Tooltip
              title={I18n.get("site_wearingMask")}
              placement="bottom"
              classes={{ tooltip: this.props.classes.tooltip }}
            >
              <img
                src={images.maskBlack}
                className={this.props.classes.headerIcon}
                alt="wearing_mask"
              />
            </Tooltip>
          ) : filter.name === "temperature" ? (
            <Tooltip
              title={I18n.get("site_temperature")}
              placement="bottom"
              classes={{ tooltip: this.props.classes.tooltip }}
            >
              <img
                src={images.temperatureIcon}
                className={this.props.classes.headerIcon}
                alt="temperature"
                height="22px"
              />
            </Tooltip>
          ) : filter.name.includes("temp") ? (
            <p>
              {I18n.get("site_" + filter.name)}
              <br />({I18n.get("site_tempVerbal")}
              /
              <br />
              {I18n.get("site_tempDisplayed")}
              /
              <br />
              {I18n.get("site_tempMeasured")})
            </p>
          ) : this.props.short && filter.name === "tailgating" ? (
            <Tooltip
              title={I18n.get("site_tailgating")}
              placement="bottom"
              classes={{ tooltip: this.props.classes.tooltip }}
            >
              <img
                src={images.tailgatingIcon}
                className={this.props.classes.headerIcon}
                alt="tailgating"
              />
            </Tooltip>
          ) : this.props.short && filter.name === "privateVehicle" ? (
            <Tooltip
              title={I18n.get("site_privateVehicle")}
              placement="bottom"
              classes={{ tooltip: this.props.classes.tooltip }}
            >
              <img
                src={images.car}
                className={this.props.classes.headerIcon}
                alt="privateVehicle"
              />
            </Tooltip>
          ) : this.props.short && parent.name !== "main" ? (
            <Grid container alignItems="center">
              {["name", "captured"].includes(filter.name) && (
                <Grid item style={{ paddingRight: "5px" }}>
                  <img
                    src={
                      [human, driver].includes(parent.name)
                        ? images.humanIconBlack
                        : parent.name === truck
                        ? images.truckIconBlack
                        : parent.name.includes(trailer)
                        ? images.trailerIconBlack
                        : parent.name.includes(container)
                        ? images.containerIconBlack
                        : null
                    }
                    className={this.props.classes.headerIcon}
                    height="20px"
                    style={{ maxWidth: "40px" }}
                    alt="icon"
                  />
                </Grid>
              )}
              {filter.name === "name" ? (
                <Grid item>{`${I18n.get("site_" + parent.name)} ${I18n.get(
                  "site_profile"
                )}`}</Grid>
              ) : filter.id ? (
                <p>{getAttributeName(filter.id)}</p>
              ) : (
                <Grid item>{I18n.get("site_" + filter.name)}</Grid>
              )}
            </Grid>
          ) : filter.name === "name" ? (
            <p>{I18n.get("site_profileName")}</p>
          ) : filter.id ? (
            <p>{getAttributeName(filter.id)}</p>
          ) : (
            <p>{I18n.get("site_" + filter.name)}</p>
          )}
          {get(this.props, ["sort", "field"]) === parent.name &&
          get(this.props, ["sort", "subField"]) === filter.name &&
          get(this.props, ["sort", "orderDesc"]) ? (
            <img src={images.bottomArrow} alt="bottomArrow" />
          ) : get(this.props, ["sort", "field"]) === parent.name &&
            get(this.props, ["sort", "subField"]) === filter.name &&
            !get(this.props, ["sort", "orderDesc"]) ? (
            <img src={images.topArrow} alt="topArrow" />
          ) : (
            ""
          )}
          {filter.img && !filter.name.includes("temp") && (
            <img
              src={filter.img}
              alt={filter.name + "_img"}
              style={{ marginBottom: 0 }}
            />
          )}
          {filter.img && filter.name.includes("temp") && (
            <img
              src={filter.img}
              alt={filter.name + "_img"}
              style={{ position: "absolute", top: "13px", right: "15px" }}
            />
          )}
        </Box>
        {!this.props.short &&
          filter.filterType === "select" &&
          this.renderSelectFilter(filter.name, parent.name)}
        {!this.props.short &&
          filter.filterType === "search" &&
          (filter.name === "ref" ? (
            <SmartSearchRef
              data={this.props.logs}
              filter={
                parent.name === "none"
                  ? [filter.name]
                  : [parent.name, filter.name]
              }
              onSelect={this.handleSearchFilterRef}
              activeFilter={get(this.props, [
                "filters",
                parent.name,
                filter.name,
                "filter",
              ])}
            />
          ) : (
            <SmartSearch
              data={this.props.logs}
              filter={
                parent.name === "none"
                  ? [filter.name]
                  : [parent.name, filter.name]
              }
              onSelect={this.handleSearchFilter}
              activeFilter={get(this.props, [
                "filters",
                parent.name,
                filter.name,
                "filter",
              ])}
            />
          ))}
        {!this.props.short && filter.filterType === "date" && (
          <DateFilter
            filter={filter.name}
            parent={parent.name}
            returnData={this.handleDateFilter}
            startDate={
              get(this.props, [
                "filters",
                parent.name,
                filter.name,
                "startDate",
              ]) || nowWithOutSeconds()
            }
            endDate={
              get(this.props, [
                "filters",
                parent.name,
                filter.name,
                "endDate",
              ]) || nowWithOutSeconds()
            }
          />
        )}
      </TableCell>
    );
  };

  renderDataCell = (row, filter, subFilter) => {
    let subFields =
      this.props.section === driveInOut
      ? driveInOutFields.subFields
      : this.props.section === walkInOut
      ? walkInOutFields.subFields
      : this.props.section === trainInOut
      ? trainInOutFields.subFields
      : null;

    return (
      <TableCell
        key={row.id + filter.name + subFilter.name}
        onClick={() => this.openTab(row, filter.name, subFilter.name)}
        classes={{
          root: this.props.classes.tableCell,
        }}
        align={
          ["tailgating", "privateVehicle", "mask", "temperature"].includes(
            subFilter.name
          ) && this.props.short
            ? "center"
            : "left"
        }
        style={{
          borderLeft:
            subFilter.name === subFields[0][0].name
              ? "none"
              : subFilter.name === subFields[filter.subFieldsId][0].name &&
                !this.props.short
              ? "1px solid " + colors.black
              : "none",
          //: "1px solid " + colors.veryLighPink,
        }}
      >
        {subFilter.name === "captured" && row[filter.name].name ? (
          row[filter.name].contextPhoto &&
          row[filter.name].contextPhoto.length > 0 ? (
            <Picture
              url={row[filter.name].contextPhoto[0].url}
              maxHeight={46}
              justify="flex-start"
              style={{
                maxHeight: "46px",
                borderRadius: "4px",
              }}
              alt="context_photo"
            />
          ) : (
            <Box className={this.props.classes.noPhotoBox}>
              <img
                src={
                  filter.name === truck
                    ? images.truckIconBlack
                    : [driver, human].includes(filter.name)
                    ? images.humanIconBlack
                    : filter.name.includes(trailer)
                    ? images.trailerIconBlack
                    : images.containerIconBlack
                }
                style={{ maxWidth: "30px" }}
                alt="icon"
              />
            </Box>
          )
        ) : subFilter.name === "verification" &&
          get(row, [filter.name, subFilter.name]) ? (
          <Grid container alignItems="center" wrap="nowrap">
            {get(row, [filter.name, subFilter.name]).map((verif, i) => (
              <Grid item key={i}>
                {get(verif, ["verification"]) === COVID_VERIFICATION && (
                  <ProfileSurveyDialog
                    title={get(verif, [
                      "survey",
                      "type",
                      `name_${this.props.language}`,
                    ])}
                    onlyOneSurvey
                    surveys={
                      get(verif, ["reference"])
                        ? { referenceSurvey: get(verif, ["survey"]) }
                        : { lastSurvey: get(verif, ["survey"]) }
                    }
                    profileId={get(row, [filter.name, "profile", "id"])}
                    language={this.props.language}
                    profileName={get(row, [filter.name, "profile", "name"])}
                    hide={{ new: true }}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        ) : subFilter.name === "mask" ? (
          <img
            src={
              get(row, [filter.name, "wearingMask"]) === true
                ? images.maskGreen
                : get(row, [filter.name, "wearingMask"]) === false
                ? images.noMaskRed
                : images.maskGrey
            }
            alt="wearing_mask"
          />
        ) : subFilter.name === "temperature" ? (
          <LiveCardAction
            section={walkInOut}
            //zoneId={get(item, ["zone_id"])}
            //sessionId={get(item, ["session_id"])}
            action={measuredTemperature}
            actionProps={{
              //hasLeft: get(item, ["timeout"]) ? true : false,
              data: get(row, ["measuredTemperature"]),
              startTime:
                new Date(
                  get(row, ["detections", filter.name, "start"])
                ).getTime() || 0,
            }}
          />
        ) : subFilter.name === "instruction" ? (
          get(row, [filter.name, "verification", 0, subFilter.name]) ? (
            <Grid container wrap="nowrap" alignItems="center">
              <Grid
                item
                style={{
                  marginRight: "5px",
                  width: "14px",
                  height: "14px",
                  backgroundColor: get(row, [
                    filter.name,
                    "verification",
                    0,
                    "instructionColor",
                  ]),
                  borderRadius: "2px",
                }}
              />
              <Grid item>
                {get(row, [filter.name, "verification", 0, subFilter.name])}
              </Grid>
            </Grid>
          ) : (
            ""
          )
        ) : !row[filter.name] ||
          (typeof row[filter.name][subFilter.name] !== "boolean" &&
            !row[filter.name][subFilter.name]) ? (
          ""
        ) : row[filter.name].tempType && subFilter.name.includes("temp") ? (
          row[filter.name][subFilter.name].map((temp, index) => (
            <p key={filter.name + subFilter.name + index} style={{ margin: 0 }}>
              {(temp || " -- ") +
                row[filter.name].tempType +
                (index !== row[filter.name][subFilter.name].length - 1
                  ? " / "
                  : "")}
            </p>
          ))
        ) : subFilter.name === "verified" ? (
          <Box className={this.props.classes.verifiedHeader}>
            {row[filter.name][subFilter.name][0] && (
              <img src={images.fuelIcon} alt="fuel_icon" />
            )}
            {row[filter.name][subFilter.name][1] && (
              <img src={images.odometerIcon} alt="odometer_icon" />
            )}
            {row[filter.name][subFilter.name][2] && (
              <img src={images.sealIcon} alt="seal_icon" />
            )}
            {row[filter.name][subFilter.name][3] && (
              <img src={images.temperatureIcon} alt="temperature_icon" />
            )}
          </Box>
        ) : row[filter.name][subFilter.name].toString().length > 50 ? (
          <Tooltip
            title={row[filter.name][subFilter.name]}
            placement="bottom"
            classes={{ tooltip: this.props.classes.tooltip }}
          >
            <span>
              {row[filter.name][subFilter.name].substr(0, 48) + "..."}
            </span>
          </Tooltip>
        ) : subFilter.name === "privateVehicle" ? (
          <Checkbox
            checked={row[filter.name][subFilter.name] === "No" ? false : true}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              this.handleCheckboxChange("privateVehicle", row, e.target.checked)
            }
            color="primary"
          />
        ) : subFilter.name === "tailgating" ? (
          <Checkbox
            checked={row[filter.name][subFilter.name]}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              this.handleCheckboxChange("tailgating", row, e.target.checked)
            }
            color="primary"
          />
        ) : subFilter.filterType === "date" ? (
          <>
            <span>{row[filter.name][subFilter.name].split(",")[0]}</span>
            <br />
            <span>{row[filter.name][subFilter.name].split(",")[1]}</span>
          </>
        ) : typeof row[filter.name][subFilter.name] === "boolean" ? (
          row[filter.name][subFilter.name] === true ? (
            I18n.get("site_yes")
          ) : (
            I18n.get("site_no")
          )
        ) : subFilter.name === "tag" ? (
          <Grid container alignItems="center" wrap="nowrap">
            <TagColor color={`#${get(row, [filter.name, "tagColor"])}`} />
            <Grid item>{row[filter.name][subFilter.name]}</Grid>
          </Grid>
        ) : subFilter.name === "name" ? (
          <Grid container alignItems="center" wrap="nowrap">
            <Grid item>
              {size(get(row, [filter.name, "profile", "images"])) > 0 ? (
                <Picture
                  url={get(
                    lodashFilter(
                      get(row, [filter.name, "profile", "images"]),
                      (img) =>
                        get(img, ["active"]) === true &&
                        (get(row, [filter.name, "profile", "type", "id"]) !==
                          1 ||
                          get(img, ["enrollment_status"]) === true)
                    ),
                    [0, "url"]
                  )}
                  maxHeight={46}
                  justify="flex-start"
                  style={{
                    maxHeight: "46px",
                    borderRadius: "4px",
                    marginRight: "10px",
                  }}
                  alt="context_photo"
                />
              ) : (
                <>
                  {isUnknownProfile(
                    get(row, [filter.name, "profile", "id"])
                  ) ? (
                    <></>
                  ) : (
                    <Box
                      className={this.props.classes.noPhotoBox}
                      style={{ marginRight: "10px" }}
                    >
                      <img
                        src={
                          filter.name === truck
                            ? images.truckIconBlack
                            : [driver, human].includes(filter.name)
                            ? images.humanIconBlack
                            : filter.name.includes(trailer)
                            ? images.trailerIconBlack
                            : images.containerIconBlack
                        }
                        style={{ maxWidth: "30px" }}
                        alt="icon"
                      />
                    </Box>
                  )}
                </>
              )}
            </Grid>
            <Grid item>{row[filter.name][subFilter.name]}</Grid>
            <Grid item>
              {row[filter.name].overriden && (
                <img
                  src={images.overrideIconGrey}
                  alt="overriden"
                  style={{ marginLeft: "10px" }}
                />
              )}
            </Grid>
          </Grid>
        ) : (
          row[filter.name][subFilter.name]
        )}
      </TableCell>
    );
  };

  renderSelectFilter = (filter, parent) => {
    if (
      !get(this.props, ["filters", parent, filter]) ||
      !get(this.props, ["filters", parent, filter, "values"])
    ) {
      return (
        <TextField
          select
          variant="outlined"
          value={"all"}
          disabled={true}
          InputProps={{
            inputProps: {
              className: this.props.classes.selectValue,
            },
          }}
          margin="dense"
          className={this.props.classes.selectFilter}
        >
          <MenuItem value={"all"}>{I18n.get("site_all")}</MenuItem>
        </TextField>
      );
    }
    return (
      <TextField
        select
        variant="outlined"
        value={this.props.filters[parent][filter].filter}
        onChange={(e) =>
          this.handleSelectFilter(e.target.value, filter, parent)
        }
        SelectProps={{
          multiple: true,
          MenuProps: {
            anchorOrigin: { vertical: "bottom", horizontal: "left" },
            getContentAnchorEl: null,
            MenuListProps: {
              className: this.props.classes.selectMenu,
            },
          },
          renderValue: (selected) =>
            selected[0] === "all"
              ? I18n.get("site_all")
              : selected
                  .map((s) =>
                    typeof s === "boolean"
                      ? I18n.get(`site_${s === true ? "yes" : "no"}`)
                      : s
                  )
                  .join(", "),
        }}
        InputProps={{
          inputProps: {
            className: this.props.classes.selectValue,
          },
        }}
        margin="dense"
        className={this.props.classes.selectFilter}
      >
        <MenuItem value={"all"} style={{ fontSize: "" }}>
          <Checkbox
            checked={
              this.props.filters[parent][filter].filter.indexOf("all") > -1
            }
            color="primary"
          />
          <ListItemText
            primary={I18n.get("site_all")}
            classes={{
              primary: this.props.classes.listItemText,
            }}
          />
        </MenuItem>
        <MenuItem value={"blank"}>
          <Checkbox
            checked={
              this.props.filters[parent][filter].filter.indexOf("blank") > -1
            }
            color="primary"
          />
          <ListItemText
            primary={I18n.get("site_blank")}
            classes={{
              primary: this.props.classes.listItemText,
            }}
          />
        </MenuItem>
        {this.props.filters[parent][filter].values.map((value, index) => (
          <MenuItem key={index} value={value}>
            <Checkbox
              checked={
                this.props.filters[parent][filter].filter.indexOf(value) > -1
              }
              color="primary"
            />
            <ListItemText
              primary={
                typeof value === "boolean"
                  ? value === true
                    ? I18n.get("site_yes")
                    : I18n.get("site_no")
                  : value
              }
              classes={{
                primary: this.props.classes.listItemText,
              }}
            />
          </MenuItem>
        ))}
      </TextField>
    );
  };

  renderPagination() {
    return (
      <Box className={this.props.classes.pagination}>
        {this.props.isLoading && (
          <Box className={this.props.classes.loader}>
            <Loader noDecoration={true} />
          </Box>
        )}
        <TablePagination
          component="div"
          classes={{
            root: this.props.classes.tableCell,
            toolbar: this.props.classes.paginationToolbar,
          }}
          rowsPerPageOptions={[5, 10, 25]}
          colSpan={3}
          //count={this.props.logs.length}
          count={this.props.totalRows}
          rowsPerPage={this.props.rowsPerPage}
          page={this.state.page}
          SelectProps={{
            native: true,
          }}
          onChangePage={this.changePage}
          onChangeRowsPerPage={this.changeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          labelRowsPerPage={I18n.get("site_rowsPerPage")}
        />
      </Box>
    );
  }

  render() {
    const { classes, short, logs } = this.props;
    
    let fields =
      this.props.section === driveInOut
        ? driveInOutFields.fields
        : this.props.section === walkInOut
        ? walkInOutFields.fields
        : this.props.section === trainInOut
        ? trainInOutFields.fields
        : null;
    let subFields =
      this.props.section === driveInOut
        ? driveInOutFields.subFields
        : this.props.section === walkInOut
        ? walkInOutFields.subFields
        : this.props.section === trainInOut
        ? trainInOutFields.subFields
        : null;
    let shortSubFields =
      this.props.section === driveInOut
        ? driveInOutFields.shortSubFields
        : this.props.section === walkInOut
        ? walkInOutFields.shortSubFields
        : this.props.section === trainInOut
        ? trainInOutFields.shortSubFields
        : null;
    let verified =
      this.props.section === driveInOut
        ? driveInOutFields.verified
        : this.props.section === walkInOut
        ? walkInOutFields.verified
        : this.props.section === trainInOut
        ? trainInOutFields.verified
        : null;

    let displayedSubFields = short ? shortSubFields : subFields;

    if (this.props.isLoading) {
      return (
        <Loader
          noDecoration={true}
          height="calc(100vh - 245px)" /*height="calc(100vh - 182px)"*/
        />
      );
    }

    // if ((!logs || Object.keys(logs).length === 0) && !this.props.filtered) {
    //   return (
    //     <Grid
    //       container
    //       alignItems="center"
    //       justify="center"
    //       className="mainContent"
    //       style={{
    //         width: "100%",
    //         height: "calc(100vh - 182px)",
    //       }}
    //     >
    //       <Grid item className="text-54">
    //         {I18n.get("site_noEntryFound")}
    //       </Grid>
    //     </Grid>
    //   );
    // }

    return (
      <Box style={{ position: "relative" }}>
        <TableContainer
        // className={
        //   short ? classes.mainContentShort : classes.mainContentLarge
        // }
        // classes={{ root: classes.tableContainer }}
        // style={{
        //   maxHeight:
        //     short && this.props.section === walkInOut
        //       ? //? "calc(100vh - 697px)"
        //         "calc(100vh - 370px)"
        //       : "",
        // }}
        >
          <Table stickyHeader>
            <TableHead>
              {!short && (
                <TableRow>
                  {fields
                    .slice(0, fields.length - 1)
                    .map((f, i) => this.renderHeaderGroupCell(f, i))}
                </TableRow>
              )}
              <TableRow
                style={{
                  backgroundColor: colors.white,
                  position: !this.props.short ? "sticky" : "initial",
                }}
              >
                {fields
                  .slice(0, short ? fields.length : fields.length - 1)
                  .map((f, indexField) => (
                    <React.Fragment key={f.name + indexField}>
                      {displayedSubFields[f.subFieldsId] &&
                        displayedSubFields[f.subFieldsId].map(
                          (sf, index) =>
                            (!this.props.displayColumns ||
                              (this.props.displayColumns[indexField] &&
                                this.props.displayColumns[indexField].includes(
                                  index
                                ))) &&
                            (this.props.open[f.name] ||
                              !f.collapsedNb ||
                              index < f.collapsedNb ||
                              short) && (
                              <React.Fragment key={f.name + sf.name}>
                                {this.renderHeader(
                                  sf,
                                  f,
                                  sf.name === "verified"
                                    ? colors.green10
                                    : colors.cardTitleBg
                                )}
                              </React.Fragment>
                            )
                        )}
                      {this.props.open.verified[f.name] &&
                        verified.map((verif) => (
                          <React.Fragment key={f.name + verif.name}>
                            {this.renderHeader(verif, f, colors.green10)}
                          </React.Fragment>
                        ))}
                    </React.Fragment>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {map(logs, (row, index) => (
                <TableRow
                  key={row.id}
                  style={{
                    cursor: "pointer",
                    backgroundColor:
                      this.props.section === walkInOut &&
                      size(
                        lodashFilter(
                          get(row, ["human", "verification"]),
                          (v) => get(v, ["status"]) === VERIFICATION_WARNING
                        )
                      ) > 0
                        ? colors.lightRed
                        : index % 2
                        ? colors.tableDarkBg
                        : colors.white,
                  }}
                  //onClick={() => this.openTab(row)}
                  hover={true}
                  classes={{ hover: classes.tableRowHover }}
                >
                  {fields
                    .slice(0, short ? fields.length : fields.length - 1)
                    .map((f, indexField) => (
                      <React.Fragment key={row.id + f.name + indexField}>
                        {displayedSubFields[f.subFieldsId] &&
                          displayedSubFields[f.subFieldsId].map(
                            (sf, i) =>
                              (!this.props.displayColumns ||
                                (this.props.displayColumns[indexField] &&
                                  this.props.displayColumns[
                                    indexField
                                  ].includes(i))) &&
                              (this.props.open[f.name] ||
                                !f.collapsedNb ||
                                i < f.collapsedNb ||
                                short) && (
                                <React.Fragment key={row.id + f.name + sf.name}>
                                  {this.renderDataCell(row, f, sf)}
                                </React.Fragment>
                              )
                          )}
                        {this.props.open.verified[f.name] &&
                          verified.map((verif) => (
                            <React.Fragment key={row.id + f.name + verif.name}>
                              {this.renderDataCell(row, f, verif)}
                            </React.Fragment>
                          ))}
                      </React.Fragment>
                    ))}
                </TableRow>
              ))}
              {this.state.emptyRows > 0 && (
                <TableRow style={{ height: 53 * this.state.emptyRows }}>
                  <TableCell
                    colSpan={6}
                    classes={{ root: classes.tableCell }}
                  />
                </TableRow>
              )}
            </TableBody>
          </Table>
          {size(logs) === 0 && (
            <Grid
              container
              alignItems="center"
              justify="center"
              className="mainContent"
              style={{
                width: "100%",
                height: "calc(100vh - 182px)",
              }}
            >
              <Grid item className="text-54">
                {I18n.get("site_noEntryFound")}
              </Grid>
            </Grid>
          )}
          {short && this.props.loadMore && (
            <Box className={classes.loadMore}>
              {this.props.isFetching ? (
                <CircularProgress size={15} />
              ) : (
                <span onClick={() => this.props.loadMore()}>
                  {I18n.get("site_loadMore")}
                </span>
              )}
            </Box>
          )}
        </TableContainer>
        {!short && this.renderPagination()}
        {!short && (
          <SiteLogCsvExport
            style={{ position: "absolute", left: "12px", bottom: "14px" }}
            columns={flatten(
              map(
                slice(fields, 0, short ? size(fields) : size(fields) - 1),
                (f, i) =>
                  map(
                    lodashFilter(
                      displayedSubFields[f.subFieldsId],
                      (sf) => get(sf, ["csv"]) !== false
                    ),
                    (sf) => ({
                      ...sf,
                      name: I18n.get(`site_${get(sf, ["name"])}`),
                    })
                  )
              )
            )}
            section={this.props.section}
          />
        )}
      </Box>
    );
  }
}

SiteLogTable.propTypes = {
  fetchMoreData: PropTypes.func,
  totalRows: PropTypes.number.isRequired,
  logs: PropTypes.array.isRequired,
  filters: PropTypes.object,
  sort: PropTypes.object,
  short: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => {
  return {
    language: state.user.language,
  };
};

export default withRouter(
  connect(mapStateToProps)(withStyles(styles)(SiteLogTable))
);
